import React, { useMemo } from 'react'
import { BarGraphV2 } from '../components/BarGraphV2';
import { updateCookie } from '../items/CookieData';
import { Link } from 'react-router-dom';
import { combine, DiffData, excludeCountries, getAllCountries, getElectionDiff2, getGroup, getPrevPoll, getShort2, getTitle, getUsedParties, withPartyOnPolls } from '../calculations/utilsV2';
import electionV2 from '../data/electionV2.json';
import { fixCountryStats, getCountryStat, getLastElection, getPrevElection } from '../calculations/calcV2';
import { LineGraphV2 } from '../components/LineGraphV2';
import { GraphHeaderV2 } from '../items/GraphHeaderV2';
import { PredictionCountryV2 } from '../items/PredictionCountryV2';
import { QuestionBoardV2 } from '../items/QuestionBordV2';
import { CountryMapV2 } from '../items/EuropeMapV2';
import { PicturableGraphV2, PicturableV2 } from '../items/PicturableV2';

interface CountryPageProps {
    country?: string,
    region?: string,
}

export const CountryPageV2 = (props: CountryPageProps) => {
    const { country, region } = props;
    const countryRegion = region ?? country ?? '';
    const countryStats = electionV2.Stats.filter(x => !excludeCountries.includes(x.Country));
    const allCountries = getAllCountries();
    fixCountryStats(countryStats, allCountries);
    const countryStat = getCountryStat(countryStats, countryRegion);
    const short = getShort2(countryRegion) ?? '';

    if (!countryStat || !countryStat.Polls) { return <div /> }

    updateCookie(short, countryStat.Polls[0].Field ?? '');

    const lastElection = getLastElection(countryStat);
    const prevElection = getPrevElection(countryStat);

    const usedParties = getUsedParties(countryStat) ?? [];
    const parties = usedParties.map(x => x.Party);
    //console.log('parties', parties);
    const groups = usedParties.map(x => x.Group);
    const electionDiff = getElectionDiff2(countryStat, 0, 1, false, false);
    const electionDiffSeats = getElectionDiff2(countryStat, 0, 1, false, true);
    if (!electionDiff || !electionDiffSeats) { return <div /> }
    //console.log('electionDiff', electionDiff);

    const funcComb = (data: DiffData, values: number[]): PartyValue[] => {
        return combine(values, data.parties, data.groups).filter(x => x.value);
    }
    
    const date = countryStat.Elections[0].Date ?? new Date().toDateString();

    return (
        <div className="countryDiv">
            <ElectionResultLink short={short} countryStat={countryStat} />
            <LineGraphV2 countryStat={countryStat}
                title={countryRegion + ' (' + countryStat.Polls[countryStat.Polls.length - 1].Year + ' - ' + countryStat.Polls[0].Year + ')'} />
            
            <PredictionCountryV2 countryRegion={countryRegion} isParliament={true} countryStat={countryStat} short={short} />
            {(["Spain", "Germany", "Italy", "Austria"].includes(countryRegion)) &&
                <CountryMapV2 countryStats={countryStats} countryStat={countryStat} />
            }
            <div className="parentOfHalf">
                <div className="half">
                    <PicturableGraphV2 downloadName="electionResult.jpg" title={countryRegion + " election result " + lastElection.Year} labels={parties} 
                        data={electionDiff.newValues} isSemiCircleGraph={true} groups={groups} createText={() => electionDiff.textDiff ?? ''} />
                    { prevElection && electionDiff.diffValues && 
                        <>
                            <span className="highlight hlFirst">{lastElection.Year} - {prevElection.Year}</span>
                            <GraphHeaderV2 diffs={funcComb(electionDiff, electionDiff.diffValues)} date={date} />
                        </>
                    }
                </div>
                <div className="half">
                    <PicturableGraphV2 downloadName="electionResultSeats.jpg" title={countryRegion + " election result seats " + lastElection.Year} labels={parties} 
                        data={electionDiffSeats.newValues} isSemiCircleGraph={true} groups={groups} createText={() => electionDiffSeats.textDiff ?? ''} />
                    { prevElection && electionDiffSeats.diffValues && 
                        <>
                            <span className="highlight hlFirst">{lastElection.Year} - {prevElection.Year}</span>
                            <GraphHeaderV2 diffs={funcComb(electionDiffSeats, electionDiffSeats.diffValues)} date={date} />
                        </>
                    }
                </div>
            </div>
            <br />
            <QuestionBoardV2 countryStat={countryStat} />
            <span className="underline">election polls</span>
            <br /><br />
            {countryStat.Polls.slice(0, 10).map((x, i) => {
                if (countryStat.Polls.slice(0, i).map(y => y.Firm).includes(x.Firm)) { return null; } // Removes the same polling firm used again
                const prev = getPrevPoll(countryStat, x.Firm ?? '', i);
                return <div key={i}>
                    { prev &&
                        <GraphHeaderV2 diffs={x.Values.map((y, i) => 
                                ({ party: countryStat.Parties[i], group: getGroup(countryStat, countryStat.Parties[i]), value: Math.round(10 * (y - prev.Values[i]))/10}))
                                .filter(x => x.value && parties.includes(x.party)).sort((a, b) => b.value - a.value)} 
                            date={x.DateTo} firm={x.Firm ?? ''} isDaysAgo={true} />
                    }
                    
                    <PollItem countryStat={countryStat} poll={x} i={i} short={short} />
                    <hr />
                </div>
            })}
        </div>
    );
}

interface ElectionResultLinkProps {
    short: string,
    countryStat: CountryStat,
}

const ElectionResultLink = (props: ElectionResultLinkProps) => {
    const { short, countryStat } = props;
    if (!short){ return null; }

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.getFullYear() === d2.getFullYear() &&
          d1.getDate() === d2.getDate() &&
          d1.getMonth() === d2.getMonth();
    }
    const lastElectionDate = getLastElection(countryStat).Date;

    return lastElectionDate && isSameDay(new Date(lastElectionDate), new Date()) ? (
        <div>
            <br />
            <Link to={"/"+short+"/result"}>
                <div className="highlight groupNi">
                    <span className="dot groupSD" />
                    &nbsp;Live election data...
                </div>
            </Link>
        </div>
    ) : null;
}

interface PollItemProps {
    countryStat: CountryStat,
    i: number,
    poll: Poll,
    short: string,
}

const PollItem = (props: PollItemProps) => {
    const { poll, i, countryStat, short } = props;
    const countryRegion = countryStat.Region ?? countryStat.Country;

    const createText = () => {
        const pollParty = withPartyOnPolls(countryStat, poll);
        return poll.Firm + "\n" + poll.Field + "\n" + pollParty.sort((a, b) => b.value - a.value)
            .map(x => x.party + " (" + x.group + "): " + x.value + "%").reduce((s, a) => s + "\n" + a, "");
    }

    const getMenu = () => {
        return (<a href={encodeURI("https://twitter.com/intent/tweet?text=https://statostuff.com/" + short + "\n\n" + createText())} target="twitter">
            <img className="hoverable" width={24} src='/twitterLogo2.png' alt='tweet' />
        </a>);
    }
    const getChildren = (isDefault: boolean) => {
        return (<>
            <img className={"floatLeft " + (isDefault ? " collapsed" : "")} src="Statostuff.png" alt="Statostuff" />
            <img className={"floatRight" + (isDefault ? " collapsed" : "")} src="mini.png" alt="legend" />
            <BarGraphV2 bgColor={isDefault ? "#333" : "#fff"} 
                title={getTitle(countryRegion, poll)} 
                countryStat={countryStat}
                pollIndex={i} />
        </>);
    }

    return <PicturableV2 getMenu={getMenu} downloadName={`poll_${countryRegion}_${poll.Field}_${poll.Firm}.png`} createText={createText} getChildren={getChildren} />
}